import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { useSelector } from 'react-redux';
import _ from 'underscore';
import UtilService from '../../../../../../../helper/service';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    marginBottom: 20,
    height: 74,
    userSelect: 'none',
  },
  pills: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: 0,
  },
  box: {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#fff',
    fontFamily: 'roboto-bold',
    fontSize: 18,
    borderRadius: 8,
    height: 45,
    marginRight: 2,
  },
  section: {
    display: 'flex',
    flexDirection: 'row',
    height: 16,
    marginBottom: 3,
  },
  pill: {
    position: 'absolute',
    width: 14,
    height: 14,
    borderRadius: 7,
    background: 'grey',
  },
}));

function OverviewPill(props: any) {

  const classes = useStyles();

  const { graphWidth, graphData, clip, marginTop } = props;
  const { analyseStart, cliped, currentSession, stimulus, settings, hiddenParticipants } = useSelector((state: any) => state.analysis);
  const { session_type, session_length } = currentSession;
  const { graphZoom } = settings;

  const graphStart = cliped ? cliped.start_clip : analyseStart;
  const refactorStart = (session_type === 'MODERATED') ? graphStart : 0;

  const { pills, immersion } = graphData
  const [result, setResult] = useState<any>([]);

  const startPoint = _.first(immersion?.data);
  const finalPoint = _.last(immersion?.data);

  const availableStart = UtilService.getLocalEpoch2(startPoint?.time_stamp || "1970-01-01 00:00:00");
  const availableEnd = UtilService.getLocalEpoch2(finalPoint?.time_stamp || "1970-01-01 00:00:00");

  const limitSeconds = 60;
  const isBlankRule = Number(session_length) <= limitSeconds;

  if (!pills) {
    return null
  }

  const newPills: any = [];

  if (pills && pills[0]?.moment && stimulus) {
    newPills.push({
      pill: "blank",
      start: 0,
      end: stimulus.stimulus_video_length,
      overall_index: '--',
      tier_indicator: "High",
      color_indicator: "gray",
      context_indicator: "",
      moments: pills
    });
  }

  useEffect(() => {
    onGetBlankPills();
  }, [hiddenParticipants, cliped]);

  const onGetBlankPills = async () => {
    const convertedPills = pills
      .filter((x: any) => x.pill === 'RED' || x.pill === 'GREEN')
      .map((u: any, k: number) => {
        if (pills.length === k + 1) {
          return { ...u, end: _.min([u.end, availableEnd]) }
        } else {
          return u
        }
      });

    const convertedData = isBlankRule ? convertedPills
      .reduce((prev: any[], c: any, index: number) => {
        const first = convertedPills[index - 1]?.end || (session_type === 'MODERATED' ? availableStart : 0);
        const second = convertedPills[index]?.start;
        if (first !== second) {
          prev.push({
            start: first,
            end: second,
            pill: 'BLANK',
          })
        }
        if (convertedPills.length === index + 1 && second !== (Number(session_length) + refactorStart) && convertedPills[index]?.end < Number(session_length) + refactorStart) {
          prev.push({
            start: convertedPills[index]?.end,
            end: _.min([Number(session_length) + refactorStart, availableEnd + refactorStart, cliped ? cliped.end_clip : availableEnd]),
            pill: 'BLANK',
          })
        }
        prev.push(c);
        return prev
      }, []) : pills;

    setResult(convertedData);

    const bigData = await Promise.all(convertedData.map(async (item: any) => {
      const length = item.end - item.start;
      const sum = _.reduce(immersion?.data, function(memo: any, num: any, index: any) {
        if (index + availableStart >= item.start && index + availableStart < item.end + 1 && item.pill === 'BLANK') {
          return memo + num?.immersion_index;
        }else{
          return memo;
        }
      }, 0) / ( length === 0 ? 1 : length + 1);
      if (item.overall_index) return item;
      return {
        ...item,
        overall_index: Math.floor(sum),
        context_indicator: 'INTERESTED'
      }
    }));
    setResult(bigData)
  };

  return (
    <div
      className={classes.root}
      style={{
        marginTop: clip ? -85 : -131,
        width: graphWidth
      }}
    >
      <div id={'overviewpills'}
        className={classes.pills}
        style={{
          width: graphWidth,
          marginTop: marginTop ? marginTop : (stimulus ? 24 : 21),
          position: 'relative'
        }}
      >
        {
          (newPills.length === 1 ? newPills : result).map((item: any, index: number) => {

            let start = item.start ? item.start : item.epoch_start;
            let end = item.end ? item.end : item.epoch_end;
            start = (cliped && index === 0 && !isBlankRule) ? cliped.start_clip : start;
            end = (cliped && (index === result.length - 1) && !isBlankRule) ? cliped.end_clip : end;
            if (!start) {
              start = 0;
            }
            if (!end) {
              end = 0;
            }
            if (cliped && stimulus && start < cliped.start_clip) {
              start = cliped.start_clip
            }
            if (cliped && stimulus && end > cliped.end_clip) {
              end = cliped.end_clip
            }

            if (end - start > 1000000) {
              return null
            }

            const getPosition = (c: any) => {
              return (c.epoch_start - (session_type === 'ASYNC' ? cliped ? cliped.start_clip : 0 : graphStart)) * 15 / Math.pow(graphZoom, 2);
            }

            return (
              <div key={index} style={{ position: 'absolute', marginBottom: 45 }}>
                <div className={classes.section}>
                  {item?.moments && item?.moments.map((c: any, k: number) => {
                    if ((cliped && (cliped.start_clip <= c.epoch_start && c.epoch_start <= cliped.end_clip)) || !cliped)
                      return (
                        <div
                          key={k}
                          id={"key_" + c.epoch_start}
                          className={classes.pill}
                          style={{
                            background: UtilService.refactorGraphColor(c.moment),
                            left: getPosition(c)
                          }}
                        />
                      )
                  })}
                </div>

                {(!isBlankRule ? (item.overall_index > 0 && item.pill !== 'Blank') : item.pill) && (
                  <div
                    aria-describedby={'simple-popover'}
                    className={classes.box}
                    id={"segment_" + item.start}
                    style={{
                      left: (start - (session_type === 'ASYNC' ? cliped ? cliped.start_clip : 0 : graphStart)) * 15 / Math.pow(graphZoom, 2),
                      width: (end - start) * 15 / Math.pow(graphZoom, 2),
                      background: UtilService.refactorGraphColor(item.pill)
                    }}
                  >
                    {(end - start) / Math.pow(graphZoom, 2) > 5 && <img src={'/assets/icons/liveanalysis/graph-icon.svg'} alt={'icon'} style={{ marginRight: 5 }} />}
                    {(end - start) / Math.pow(graphZoom, 2) > 2 && (end > start) && item.overall_index > 0 && item.overall_index}
                  </div>
                )}
              </div>)
          })
        }
      </div>

    </div>
  );
}

export default OverviewPill;